import { CardRenderer } from '@/components/elements/Card';
import { EmblaCarousel } from '@/components/elements/Carousel/Carousel';
import Container from '@/components/global/Container';
import { IComponentBlocksCarouselStories, IStory } from '@/graphql/__generated__/global/types';
import { CardImageAspectRatio } from '@/helpers/determineAspectRatioByCardSpan';
import { findFullSlug } from '@/lib/strapiQueries';

function centeredScrollBarProgress(scrollProgress: number) {
    if (scrollProgress < 0.5) {
        return scrollProgress + 0.5;
    } else {
        return scrollProgress - 0.5;
    }
}

function opacityScaler(x: number) {
    // Roughly scales 1 -> 1, 0.95 -> 0.85, 0.8 -> 0.48 and caps at 0.25
    return Math.min(0.2 + 0.0035 * Math.exp(5.5 * x), 1);
}
export const CenteredSlider = ({ stories }: IComponentBlocksCarouselStories) => {
    return (
        <Container paddingX={false} maxWidth="xl">
            <EmblaCarousel
                tweenFactor={stories && stories.data?.length > 4 ? 0.4 : 0}
                emblaOptions={{
                    align: 'center',
                    slidesToScroll: 1,
                    skipSnaps: true,
                    containScroll: 'trimSnaps',
                    loop: true,
                }}
            >
                {({ emblaRef, tweenValues, scrollSnaps, scrollTo, scrollProgress, scrollPrev, scrollNext }) => {
                    const centeredScrollProgress = centeredScrollBarProgress(scrollProgress);
                    return (
                        <>
                            <EmblaCarousel.Slides ref={emblaRef} className="embla__stories">
                                {stories &&
                                    stories.data?.length > 0 &&
                                    stories.data?.map((card, index) => {
                                        const { hero } = card.attributes as IStory;
                                        const { title, description, image, video } = hero ?? {};

                                        const { playback_id } = video?.backgroundVideo?.data?.attributes ?? {};
                                        let hoverImage;
                                        let thumbnail;

                                        if (playback_id) {
                                            // Start time is set to 10s to avoid the first frame of the video
                                            hoverImage = `https://image.mux.com/${playback_id}/animated.webp?start=10&width=480&fps=24`;
                                        }

                                        if (!image && playback_id) {
                                            thumbnail = `https://image.mux.com/${playback_id}/thumbnail.webp?width=480&fps=24`;
                                        }

                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        const slug = findFullSlug(card).join('/');

                                        const link = {
                                            id: card.id as string,
                                            linkToExternalPage: slug,
                                            label: 'Read More',
                                        };

                                        return (
                                            <div className="embla__slide" key={index}>
                                                <div
                                                    className="embla__scale"
                                                    style={{
                                                        ...(tweenValues.length && {
                                                            transform: `scale(${tweenValues[index]})`,
                                                            opacity: opacityScaler(tweenValues[index]),
                                                            filter: `contrast(${tweenValues[index]})`,
                                                        }),
                                                    }}
                                                >
                                                    <CardRenderer
                                                        card={{
                                                            title,
                                                            image: image?.image || thumbnail,
                                                            hoverImage: hoverImage,
                                                            deferHoverImageLoad: true,
                                                            description,
                                                            eyebrowText: 'Story guide',
                                                            link,
                                                            aspectRatio: CardImageAspectRatio.slim,
                                                            sizes: `(max-width: 639px) 80vw, (max-width: 1023px) 45vw, (max-width: 1919px) 30vw, 551px`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </EmblaCarousel.Slides>
                            {stories && stories.data?.length > 4 && (
                                <Container maxWidth="xl">
                                    <EmblaCarousel.ProgressBar
                                        max={scrollSnaps.length}
                                        scrollTo={scrollTo}
                                        centered={true}
                                        scrollProgress={centeredScrollProgress}
                                        scrollNext={scrollNext}
                                        scrollPrev={scrollPrev}
                                    />
                                </Container>
                            )}
                        </>
                    );
                }}
            </EmblaCarousel>
        </Container>
    );
};

export default CenteredSlider;
